import type { PermissionFlag } from '../contracts/chat-permissions.js';

/** Plural: escolhido por `Intl.PluralRules`. */
export type Plural = { one: string; other: string };
export type Dictionary = { [key: string]: string | Plural | Dictionary };

/**
 * Textos do chat em português do Brasil. A estrutura serve para outros
 * idiomas (en, es): mesmo formato, mesmas chaves.
 */
export const ptBR = {
  status: {
    online: 'Disponível',
    idle: 'Ausente',
    dnd: 'Não perturbe',
    invisible: 'Invisível',
    offline: 'Offline',
  },
  conexao: {
    conectando: 'Conectando…',
    reconectando: 'Sem conexão. Tentando de novo em {segundos} s…',
    tentarAgora: 'Tentar agora',
    sessaoEncerrada: 'Sua sessão terminou. Entre de novo.',
    versaoAntiga: 'Esta versão ficou antiga. Atualize para continuar.',
  },
  compositor: {
    placeholder: 'Conversar em #{canal}',
    placeholderDm: 'Conversar com @{nome}',
    semPermissao: 'Você não tem permissão para enviar mensagens neste canal.',
    castigo: 'Você está de castigo até {quando}.',
    pendente: 'Aceite as regras do servidor para começar a conversar.',
    verRegras: 'Ver as regras',
    threadTravada: 'Esta thread está arquivada e travada.',
    modoLento: 'Modo lento: uma mensagem a cada {duracao}.',
    respondendo: 'Respondendo a {nome}',
    editando: 'Editando a mensagem',
  },
  mensagem: {
    editada: '(editada)',
    enviando: 'Enviando…',
    naFila: 'Na fila: sai quando a conexão voltar.',
    falhou: 'Não foi enviada. Tente de novo.',
    apagada: 'Mensagem original apagada.',
    novas: 'Novas',
    hoje: 'Hoje às {hora}',
    ontem: 'Ontem às {hora}',
    encaminhada: 'Encaminhada',
    fixada: 'Fixada',
  },
  digitando: {
    um: '{a} está digitando…',
    dois: '{a} e {b} estão digitando…',
    tres: '{a}, {b} e {c} estão digitando…',
    varios: 'Várias pessoas estão digitando…',
  },
  sistema: {
    adicionou: '{autor} adicionou {alvo} ao grupo.',
    removeu: '{autor} removeu {alvo} do grupo.',
    saiu: '{autor} saiu do grupo.',
    renomeou: '{autor} mudou o nome do canal: {nome}',
    iconeGrupo: '{autor} mudou o ícone do grupo.',
    fixou: '{autor} fixou uma mensagem neste canal.',
    entrou: [
      '{autor} entrou no servidor.',
      'Boas-vindas, {autor}!',
      '{autor} chegou.',
      'Que bom ter você aqui, {autor}.',
      '{autor} acabou de entrar.',
    ].join('|'),
    impulsionou: '{autor} impulsionou o servidor!',
    seguiu: '{autor} passou a seguir {nome}.',
    threadCriada: '{autor} começou uma thread: {nome}.',
    inicioThread: 'Início da thread',
    moderacaoBloqueou:
      'A moderação automática bloqueou uma mensagem de {autor}.',
    moderacaoAlertou:
      'A moderação automática sinalizou uma mensagem de {autor}.',
    enqueteEncerrada: 'A enquete de {autor} terminou: {pergunta}',
    chamada: '{autor} começou uma chamada.',
  },
  erros: {
    generico: 'Algo deu errado. Tente de novo.',
    semConexao: 'Sem conexão com o servidor.',
    10003: 'Canal não encontrado.',
    10004: 'Servidor não encontrado.',
    10006: 'Este convite não vale mais.',
    10007: 'Essa pessoa não está no servidor.',
    10008: 'Mensagem não encontrada.',
    10011: 'Cargo não encontrado.',
    10013: 'Pessoa não encontrada.',
    10014: 'Emoji não encontrado.',
    10026: 'Essa pessoa não está banida.',
    10060: 'Figurinha não encontrada.',
    10066: 'Regra não encontrada.',
    10070: 'Evento não encontrado.',
    20016: 'Modo lento: espere para enviar de novo.',
    30001: 'Você chegou ao limite de 100 servidores.',
    30003: 'Este canal chegou ao limite de 250 fixadas.',
    30005: 'O servidor chegou ao limite de 250 cargos.',
    30008: 'O servidor chegou ao limite de emojis.',
    30010: 'A mensagem chegou ao limite de 20 reações diferentes.',
    30013: 'O servidor chegou ao limite de 500 canais.',
    30016: 'O servidor chegou ao limite de 1.000 convites.',
    30039: 'O servidor chegou ao limite de figurinhas.',
    40005: 'Arquivo grande demais.',
    40007: 'Você foi banido deste servidor.',
    50001: 'Você não tem acesso a isso.',
    50005: 'Não dá para editar a mensagem de outra pessoa.',
    50006: 'Não dá para enviar uma mensagem vazia.',
    50007: 'Não dá para mandar mensagem para essa pessoa.',
    50008: 'Não dá para enviar mensagem neste tipo de canal.',
    50013: 'Você não tem permissão para isso.',
    50021: 'Não dá para fazer isso com uma mensagem do sistema.',
    50024: 'Não dá para fazer isso neste tipo de canal.',
    50028: 'Cargo inválido.',
    50034: 'Só dá para apagar em massa mensagens com até 14 dias.',
    50035: 'Algum campo não está certo.',
    50055: 'O dono não sai do servidor: transfira a posse ou exclua.',
    50074: 'Este canal é exigido pelo servidor (regras ou avisos).',
    50083: 'A thread está arquivada e travada.',
    160004: 'Já existe uma thread para esta mensagem.',
    200000: 'A mensagem foi bloqueada pela moderação automática.',
    500000: 'Ninguém da lista pôde ser banido.',
    520001: 'A enquete já terminou.',
    520006: 'Esta mensagem não tem enquete.',
  },
  notificacoes: {
    todas: 'Todas as mensagens',
    mencoes: 'Só @menções',
    nada: 'Nada',
    padrao: 'Padrão do servidor',
    silenciado: 'Silenciado',
    silenciadoAte: 'Silenciado até {quando}',
  },
  canais: {
    0: 'Canal de texto',
    1: 'Mensagem direta',
    3: 'Grupo',
    4: 'Categoria',
    5: 'Canal de anúncios',
    10: 'Thread de anúncio',
    11: 'Thread',
    12: 'Thread privada',
    15: 'Fórum',
    16: 'Canal de mídia',
  },
  tempo: {
    agora: 'agora',
    segundos: { one: '{n} segundo', other: '{n} segundos' },
    minutos: { one: '{n} minuto', other: '{n} minutos' },
    horas: { one: '{n} hora', other: '{n} horas' },
    dias: { one: '{n} dia', other: '{n} dias' },
    meses: { one: '{n} mês', other: '{n} meses' },
    anos: { one: '{n} ano', other: '{n} anos' },
    ha: 'há {duracao}',
    em: 'em {duracao}',
  },
  mencoes: {
    mais: { one: 'mais {n} menção', other: 'mais {n} menções' },
  },
} satisfies Dictionary;

/**
 * Nome e descrição de cada permissão (tela de cargos e de permissões do
 * canal), com os nomes que o Discord usa em português.
 */
export const permissionTexts: Record<
  PermissionFlag,
  { name: string; description: string }
> = {
  CREATE_INSTANT_INVITE: {
    name: 'Criar convite',
    description: 'Permite convidar pessoas para o servidor.',
  },
  KICK_MEMBERS: {
    name: 'Expulsar membros',
    description:
      'Permite tirar pessoas do servidor (elas podem voltar com convite).',
  },
  BAN_MEMBERS: {
    name: 'Banir membros',
    description: 'Permite banir pessoas e apagar as mensagens recentes delas.',
  },
  ADMINISTRATOR: {
    name: 'Administrador',
    description:
      'Todas as permissões, em todos os canais, passando por cima das restrições. Dê com cuidado.',
  },
  MANAGE_CHANNELS: {
    name: 'Gerenciar canais',
    description: 'Permite criar, editar e apagar canais.',
  },
  MANAGE_GUILD: {
    name: 'Gerenciar servidor',
    description: 'Permite mudar o nome e as configurações do servidor.',
  },
  ADD_REACTIONS: {
    name: 'Adicionar reações',
    description:
      'Permite reagir com emojis novos (os que já estão, qualquer um usa).',
  },
  VIEW_AUDIT_LOG: {
    name: 'Ver registro de auditoria',
    description: 'Permite ver quem mudou o quê no servidor.',
  },
  PRIORITY_SPEAKER: {
    name: 'Voz prioritária',
    description: 'Voz (fora da v1).',
  },
  STREAM: { name: 'Vídeo', description: 'Voz e vídeo (fora da v1).' },
  VIEW_CHANNEL: {
    name: 'Ver canais',
    description: 'Permite ver o canal e ler as mensagens que chegam.',
  },
  SEND_MESSAGES: {
    name: 'Enviar mensagens',
    description: 'Permite escrever nos canais de texto.',
  },
  SEND_TTS_MESSAGES: {
    name: 'Enviar mensagens de texto para voz',
    description: 'Permite mensagens lidas em voz alta (/tts).',
  },
  MANAGE_MESSAGES: {
    name: 'Gerenciar mensagens',
    description: 'Permite apagar mensagens de outras pessoas.',
  },
  EMBED_LINKS: {
    name: 'Inserir links',
    description: 'Mostra a prévia dos links enviados.',
  },
  ATTACH_FILES: {
    name: 'Anexar arquivos',
    description: 'Permite enviar arquivos e imagens.',
  },
  READ_MESSAGE_HISTORY: {
    name: 'Ver histórico de mensagens',
    description: 'Permite ler as mensagens antigas do canal.',
  },
  MENTION_EVERYONE: {
    name: 'Mencionar @everyone, @here e todos os cargos',
    description: 'Permite avisar todo mundo de uma vez.',
  },
  USE_EXTERNAL_EMOJIS: {
    name: 'Usar emojis externos',
    description: 'Permite usar emojis de outros servidores.',
  },
  VIEW_GUILD_INSIGHTS: {
    name: 'Ver análises do servidor',
    description: 'Permite ver as estatísticas do servidor.',
  },
  CONNECT: { name: 'Conectar', description: 'Voz (fora da v1).' },
  SPEAK: { name: 'Falar', description: 'Voz (fora da v1).' },
  MUTE_MEMBERS: { name: 'Silenciar membros', description: 'Voz (fora da v1).' },
  DEAFEN_MEMBERS: {
    name: 'Ensurdecer membros',
    description: 'Voz (fora da v1).',
  },
  MOVE_MEMBERS: { name: 'Mover membros', description: 'Voz (fora da v1).' },
  USE_VAD: { name: 'Usar detecção de voz', description: 'Voz (fora da v1).' },
  CHANGE_NICKNAME: {
    name: 'Mudar apelido',
    description: 'Permite mudar o próprio apelido no servidor.',
  },
  MANAGE_NICKNAMES: {
    name: 'Gerenciar apelidos',
    description: 'Permite mudar o apelido de outras pessoas.',
  },
  MANAGE_ROLES: {
    name: 'Gerenciar cargos',
    description:
      'Permite criar e editar cargos abaixo do seu e dar ou tirar esses cargos. Nos canais, é "Gerenciar permissões".',
  },
  MANAGE_WEBHOOKS: {
    name: 'Gerenciar webhooks',
    description: 'Fora deste projeto.',
  },
  MANAGE_GUILD_EXPRESSIONS: {
    name: 'Gerenciar expressões',
    description: 'Permite editar e apagar emojis e figurinhas.',
  },
  USE_APPLICATION_COMMANDS: {
    name: 'Usar comandos de aplicativo',
    description: 'Fora deste projeto.',
  },
  REQUEST_TO_SPEAK: {
    name: 'Pedir para falar',
    description: 'Voz (fora da v1).',
  },
  MANAGE_EVENTS: {
    name: 'Gerenciar eventos',
    description: 'Permite editar e cancelar eventos de outras pessoas.',
  },
  MANAGE_THREADS: {
    name: 'Gerenciar threads',
    description:
      'Permite renomear, arquivar, travar e apagar threads e ver as privadas.',
  },
  CREATE_PUBLIC_THREADS: {
    name: 'Criar threads públicas',
    description: 'Permite abrir threads que todos do canal veem.',
  },
  CREATE_PRIVATE_THREADS: {
    name: 'Criar threads privadas',
    description: 'Permite abrir threads só para quem for convidado.',
  },
  USE_EXTERNAL_STICKERS: {
    name: 'Usar figurinhas externas',
    description: 'Permite usar figurinhas de outros servidores.',
  },
  SEND_MESSAGES_IN_THREADS: {
    name: 'Enviar mensagens em threads',
    description: 'Permite escrever nas threads.',
  },
  USE_EMBEDDED_ACTIVITIES: {
    name: 'Usar atividades',
    description: 'Fora da v1.',
  },
  MODERATE_MEMBERS: {
    name: 'Castigar membros',
    description: 'Permite deixar alguém só lendo por um tempo (até 28 dias).',
  },
  VIEW_CREATOR_MONETIZATION_ANALYTICS: {
    name: 'Ver análises de monetização',
    description: 'Não se aplica.',
  },
  USE_SOUNDBOARD: { name: 'Usar sons', description: 'Voz (fora da v1).' },
  CREATE_GUILD_EXPRESSIONS: {
    name: 'Criar expressões',
    description: 'Permite enviar emojis e figurinhas novos.',
  },
  CREATE_EVENTS: {
    name: 'Criar eventos',
    description: 'Permite marcar eventos e editar os próprios.',
  },
  USE_EXTERNAL_SOUNDS: {
    name: 'Usar sons externos',
    description: 'Voz (fora da v1).',
  },
  SEND_VOICE_MESSAGES: {
    name: 'Enviar mensagens de voz',
    description: 'Permite mandar áudios gravados.',
  },
  SET_VOICE_CHANNEL_STATUS: {
    name: 'Status do canal de voz',
    description: 'Voz (fora da v1).',
  },
  SEND_POLLS: {
    name: 'Criar enquetes',
    description: 'Permite mandar enquetes.',
  },
  USE_EXTERNAL_APPS: {
    name: 'Usar aplicativos externos',
    description: 'Fora deste projeto.',
  },
  PIN_MESSAGES: {
    name: 'Fixar mensagens',
    description: 'Permite fixar e desafixar mensagens.',
  },
  BYPASS_SLOWMODE: {
    name: 'Ignorar o modo lento',
    description: 'Permite escrever sem esperar o modo lento.',
  },
};
